/**
 * Atelier Royal: header.js: <ar-header>.
 * Adds [data-scrolled] once the page has scrolled, which turns the transparent
 * "over the hero" header (.ar-header--overlay, home page only) into the solid one.
 * Menu drawer, dropdowns and the cart link are handled by global.js; this module only clones the
 * lazy localization forms (template[data-ar-hydrate]) when their dropdown or drawer opens.
 */
class ARHeader extends HTMLElement {
  connectedCallback() {
    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.toggleAttribute('data-scrolled', window.scrollY > 8);
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
}

if (!customElements.get('ar-header')) customElements.define('ar-header', ARHeader);

/*
 * Lazy header content: <template data-ar-hydrate> inside the localization dropdown and the menu drawer
 * holds the country/language form (~240 <option>s). It is cloned into place the first time its
 * dropdown or drawer is about to open, so the DOM carries one live copy (the footer's) instead of three.
 * global.js handles the form by delegation, so the clone needs no wiring.
 */
const hydrate = (root) => {
  root?.querySelectorAll?.('template[data-ar-hydrate]').forEach((tpl) => tpl.replaceWith(tpl.content.cloneNode(true)));
};
if (!window.ARHeaderHydrate) {
  window.ARHeaderHydrate = true;
  // Before the dropdown opens (pointer or keyboard on its summary); `toggle` covers anything else.
  const onIntent = (event) => hydrate(event.target.closest?.('details[data-ar-dropdown]'));
  document.addEventListener('pointerdown', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('toggle', (event) => event.target.open && hydrate(event.target), true);
  document.addEventListener('ar:modal:open', (event) => hydrate(event.target));
  // Theme editor: a re-rendered header section brings fresh templates; they hydrate on the next open.
}
